// #v-ifdef IS_PROD='true'
console.error('Cheats tryed to enable on prod');
// #v-else
import { settingsStore } from '../stores/settings.store';
import { type MagicSlot as Application } from '../MagicSlot';
import { getUrlParam } from '@magic/utils';
import { apiStore } from '../stores/api.store';
import type { RoundState } from './api.types';
import { DevTools, type DebugFields, type FolderApi } from '@magic/utils';

export class Cheats {
    private app: Application;

    private devTools: DevTools;

    constructor(app: Application) {
        this.app = app;

        const settings = settingsStore.getState();

        this.devTools = new DevTools({
            position: { x: 'left', y: 'top' },
            pixi: this.app.pixi,
        });

        this.devTools.debugFolder.addBinding(settings, 'soundDebug');
        this.devTools.debugFolder.addBinding(settings, 'spineDebug');
        this.devTools.debugFolder.addBinding(settings, 'wsDebug');
        this.devTools.debugFolder.addBinding(settings, 'storeDebug');
        this.devTools.debugFolder.addBinding(settings, 'roundDebug');
        this.devTools.addSection({
            title: 'TestTools',
            expanded: false,
            fields: {},
        });

        if (settingsStore.getState().storeDebug) {
            // log initial state
            console.log(`💾 Store`, apiStore.getState());

            apiStore.subscribe((state, prevState) => {
                // show only differences
                const diffs: Record<string, unknown> = {};
                for (const key in state) {
                    if (state[key as keyof typeof state] !== prevState[key as keyof typeof state]) {
                        diffs[key] = state[key as keyof typeof state];
                    }
                }

                if (diffs && Object.keys(diffs).length === 0) {
                    return;
                }

                if (diffs.roundState) {
                    console.log(`💾 Store ${(diffs.roundState as RoundState).type}`, diffs);
                } else {
                    console.log(`💾 Store`, diffs);
                }
            });
        }

        if (settings.spineDebug) {
            setTimeout(() => {
                this.addSpineDebugSection();
            }, 1000);
        }

        if (!getUrlParam('sessionID')) {
            document.location += `?sessionID=demo&lang=en&currency=USD&device=desktop&social=false&demo=true`;
        }

        this.addSettings();
    }

    private addSettings() {
        const pane = this.devTools.settingsFolder;

        // local proxy object for Tweakpane binding
        const proxy = {
            muted: settingsStore.getState().muted,
        };

        pane.addBinding(proxy, 'muted', {
            title: 'Muted',
        }).on('change', ({ value }) => {
            settingsStore.setState({ muted: value });
        });
    }

    private addSpineDebugSection() {
        const { section } = this.devTools.addSection({
            title: 'Spine',
            expanded: !!localStorage.getItem('spineDebugExpanded'),
            fields: this.getSpineAnimationsControlls(),
        });

        section.on('fold', () => {
            if (section.expanded) {
                localStorage.setItem('spineDebugExpanded', 'true');
            } else {
                localStorage.removeItem('spineDebugExpanded');
            }
        });

        this.addSpineControllers(section);
    }

    private getSpineAnimationsControlls(): DebugFields {
        const fields: DebugFields = {};
        const spine = this.app.mainController?.spine;

        if (!spine) return {};

        spine.animations.getAll().forEach((animation: string) => {
            fields[animation] = {
                title: animation,
                type: 'button',
                cb: () => {
                    spine.animations.playByName(animation, true, 1000);
                },
            };
        });

        return fields;
    }

    private addSpineControllers(section: FolderApi) {
        const spine = this.app.mainController?.spine;

        if (!spine) return {};

        const settings = settingsStore.getState();
        const proxy = { speed: settings.spineSpeed };

        spine.speed = settings.spineSpeed;

        section
            .addBinding(proxy, 'speed', {
                label: 'Speed',
                min: 0,
                max: 1,
            })
            .on('change', ({ value }) => {
                spine.speed = value;
                settingsStore.setState({ spineSpeed: Number(value.toFixed(2)) });
            });
    }
}
// #v-endif
